import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchCountries, fetchEntities, fetchRenewals } from "../lib/dataFetchers";
import { WorldMap } from "../components/WorldMap";
import { RenewalCalendar } from "../components/RenewalCalendar";
import { RegionTilesRow } from "../components/RegionTilesRow";
import { CountriesRail } from "../components/CountriesRail";
import { EntitiesGrid } from "../components/EntitiesGrid";
import { useSession } from "../lib/auth";
import type { Region } from "../lib/types";
import { getRegionColor } from "../lib/regionColors";

// PPP §B6 — no hardcoded list; derived from entity data at render time.

export default function Home(): JSX.Element {
  const { t, i18n } = useTranslation("entities");
  const { user } = useSession();
  const navigate = useNavigate();

  // Phase TTT: lifted region-filter state shared by tiles + map + legend.
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null);
  const toggleRegion = (r: Region | null) =>
    setSelectedRegion((cur) => (cur === r ? null : r));

  const { data: entitiesRes } = useQuery({
    queryKey: ["entities-all"],
    queryFn: () => fetchEntities({ limit: 100 }),
    staleTime: 60_000,
  });
  const { data: renewals } = useQuery({
    queryKey: ["renewals"],
    queryFn: () => fetchRenewals(),
    staleTime: 60_000,
  });
  const { data: countries = [] } = useQuery({
    queryKey: ["countries"],
    queryFn: fetchCountries,
    staleTime: 60_000,
  });

  const entities = useMemo(
    () => entitiesRes?.entities ?? [],
    [entitiesRes],
  );
  const firstName = user?.name?.split(" ")[0] ?? "there";

  // Countries with at least one active entity — derived from entity data so it
  // updates on delete (the country *catalog* has 40 rows regardless of entities).
  const countryCount = useMemo(
    () => new Set(entities.map((e) => e.country?.name).filter(Boolean)).size,
    [entities],
  );

  // Urgent renewals count — PPP §B2: count red items only
  const criticalCount = useMemo(
    () =>
      (renewals ?? []).filter(
        (r) => r.severity === "red" || r.daysUntil <= 30,
      ).length,
    [renewals],
  );

  // Region stats: entity + unique-country counts per region — PPP §B6 data-driven
  const regionStats = useMemo(() => {
    const acc = new Map<Region, { entities: Set<string>; countries: Set<string> }>();
    for (const e of entities) {
      if (!e.region) continue;
      if (!acc.has(e.region)) {
        acc.set(e.region, { entities: new Set(), countries: new Set() });
      }
      const bucket = acc.get(e.region)!;
      bucket.entities.add(e.id);
      if (e.country?.name) bucket.countries.add(e.country.name);
    }
    return Array.from(acc.entries()).map(([region, bucket]) => ({
      region,
      entities: bucket.entities.size,
      countries: bucket.countries.size,
    }));
  }, [entities]);

  // Country → entity count for WorldMap heat + CountriesRail badges
  const entitiesByCountry = useMemo(() => {
    const m: Record<string, number> = {};
    for (const e of entities) {
      const n = e.country?.name;
      if (n) m[n] = (m[n] ?? 0) + 1;
    }
    return m;
  }, [entities]);

  // Entities sorted region → country → name for the grid (unfiltered, for the base)
  const sortedEntities = useMemo(
    () =>
      [...entities].sort((a, b) => {
        if (a.region !== b.region) return a.region.localeCompare(b.region);
        const ca = a.country?.name ?? "";
        const cb = b.country?.name ?? "";
        if (ca !== cb) return ca.localeCompare(cb);
        return a.name.localeCompare(b.name);
      }),
    [entities],
  );

  // §7 — filter entities and countries by selectedRegion when active
  const filteredEntities = useMemo(
    () =>
      selectedRegion
        ? sortedEntities.filter((e) => e.region === selectedRegion)
        : sortedEntities,
    [sortedEntities, selectedRegion],
  );

  const filteredCountries = useMemo(
    () =>
      selectedRegion
        ? countries.filter((c) => c.region === selectedRegion)
        : countries,
    [countries, selectedRegion],
  );

  // entitiesByCountry for CountriesRail is derived from filteredEntities
  const filteredEntitiesByCountry = useMemo(() => {
    if (!selectedRegion) return entitiesByCountry;
    const m: Record<string, number> = {};
    for (const e of filteredEntities) {
      const n = e.country?.name;
      if (n) m[n] = (m[n] ?? 0) + 1;
    }
    return m;
  }, [selectedRegion, filteredEntities, entitiesByCountry]);

  // Today's date + weekday for the header (localized; e.g. "Saturday, 13 June 2026").
  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString(i18n.language, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    [i18n.language],
  );

  return (
    <div className="max-w-7xl mx-auto">
      {/* Page header — proto .page-h */}
      <div className="flex items-end justify-between gap-[14px] mb-[18px] flex-wrap">
        <div>
          {/* h1 picks up font-display via globals.css h1 rule */}
          <h1 className="text-[24px] leading-[1.1] m-0">
            {t("home_greeting", { name: firstName })}
          </h1>
          <p className="text-[13px] text-muted mt-[5px]">
            {t("home_subtitle_entities", { count: entitiesRes?.total ?? entities.length })}
            {" "}·{" "}
            {t("home_subtitle_countries", { count: countryCount > 0 ? countryCount : countries.length })}
            {" "}·{" "}
            {criticalCount > 0 ? (
              <span className="text-red font-semibold">
                {t("home_renewals_attention", { count: criticalCount })}
              </span>
            ) : (
              <span>{t("home_no_attention")}</span>
            )}
          </p>
        </div>
        {/* Today's date + weekday — right-hand counterpart to the greeting */}
        <p
          className="text-[14px] font-semibold text-text text-right whitespace-nowrap"
          aria-label={todayLabel}
        >
          {todayLabel}
        </p>
      </div>

      {/* Region tiles — proto .tiles */}
      <RegionTilesRow
        stats={regionStats}
        selectedRegion={selectedRegion}
        onSelectRegion={toggleRegion}
      />

      {/* §7 Active region filter indicator */}
      {selectedRegion && (
        <div className="flex items-center gap-2 mb-3 text-[13px] text-muted">
          <span>
            {t("home_filter_showing", { region: selectedRegion.replace(/_/g, " ") })}
          </span>
          <button
            type="button"
            onClick={() => toggleRegion(selectedRegion)}
            aria-label={t("home_filter_clear_aria")}
            className="text-accent hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20 rounded text-[12.5px] font-medium"
          >
            {t("home_filter_clear")}
          </button>
        </div>
      )}

      {/* Countries rail + World Map — proto .home-split */}
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-[14px] mb-4">
        <CountriesRail countries={filteredCountries} entitiesByCountry={filteredEntitiesByCountry} />

        {/* World map panel — proto .panel */}
        <div
          className="bg-surface border border-border rounded-[11px] shadow-card overflow-hidden flex flex-col"
          aria-labelledby="globe-h"
        >
          {/* Card header — proto .card-h */}
          <div className="flex items-center gap-2 px-4 py-[13px] border-b border-border-2 shrink-0">
            <h3
              id="globe-h"
              className="text-[12.5px] font-bold tracking-[.03em] uppercase text-muted"
            >
              {t("global_presence")}
            </h3>
            <span className="ml-auto text-[11px] font-semibold text-dim bg-surface-2 rounded-full px-[9px] py-0.5 tabular-nums">
              {countries.length > 0 ? countries.length : 40}
            </span>
          </div>

          {/* Map body */}
          <div className="p-2 flex-1">
            <WorldMap
              entitiesByCountry={entitiesByCountry}
              countries={countries}
              height={340}
              selectedRegion={selectedRegion}
              onCountryClick={(countryName) =>
                navigate(`/entities?country=${encodeURIComponent(countryName)}`)
              }
            />
          </div>

          {/* Map legend — proto .map-legend */}
          <div className="flex flex-wrap gap-[13px] px-4 py-[11px] border-t border-border-2 shrink-0">
            {regionStats.map((s) => {
              const isActive = selectedRegion === s.region;
              const colorHex = getRegionColor(s.region, "hex");
              return (
                <button
                  key={s.region}
                  type="button"
                  aria-label={`Toggle map highlight for ${s.region}`}
                  aria-pressed={isActive}
                  onClick={() => toggleRegion(s.region)}
                  className={[
                    "flex items-center gap-[6px] text-[11.5px] text-muted",
                    "transition-colors rounded-md px-1 py-0.5 -mx-1",
                    isActive ? "text-text font-semibold" : "hover:text-text",
                  ].join(" ")}
                >
                  <span
                    aria-hidden
                    className="w-[9px] h-[9px] rounded-[3px] shrink-0"
                    style={{ background: colorHex }}
                  />
                  {s.region.replace("_", " ")}
                  <b className="font-semibold text-text tabular-nums ml-0.5">{s.entities}</b>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Renewal calendar — proto .panel .cal-wrap */}
      <RenewalCalendar renewals={renewals ?? []} />

      {/* All entities grid — §7 filtered when region active */}
      <EntitiesGrid entities={filteredEntities} />
    </div>
  );
}
